import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heartbeat, Bug, CheckCircle, Warning } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * Mini-tarjeta de salud de la app para el admin dashboard:
 * - Health score (0-100) derivado de crash_rate_pct
 * - Sesiones 7d (completadas/total)
 * - Errores 7d
 * - Últimos 5 errores reportados con timestamp + contexto
 */
export default function AppHealthCard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/app-health")
      .then(({ data }) => setData(data))
      .catch(() => { /* silent */ })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data) return null;

  const score = data.health_score;
  const scoreColor =
    score == null ? "#A3A3A3" : score >= 95 ? "#32D74B" : score >= 80 ? "#FFD60A" : score >= 50 ? "#FF9500" : "#FF3B30";
  const scoreLabel =
    score == null ? "Sin datos" : score >= 95 ? "Óptimo" : score >= 80 ? "Saludable" : score >= 50 ? "Inestable" : "Crítico";

  return (
    <div className="card-base p-5" data-testid="app-health-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Heartbeat size={20} weight="duotone" className="text-primary-app" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted">Salud de la App</div>
            <h3 className="font-display font-black uppercase text-lg tracking-tight">Últimos 7 días</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-widest"
            style={{ backgroundColor: scoreColor, color: score != null && score >= 80 && score < 95 ? "#000" : "#fff" }}
            data-testid="app-health-status"
          >
            {scoreLabel}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-[#0A0A0A] border border-app p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted">Health Score</div>
          <div className="font-display font-black text-3xl mt-1" style={{ color: scoreColor }} data-testid="health-score">
            {score == null ? "—" : `${score}`}
          </div>
          <div className="text-[10px] text-muted mt-0.5">
            {data.crash_rate_pct == null ? "sin sesiones" : `${data.crash_rate_pct}% crashes`}
          </div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted flex items-center gap-1">
            <CheckCircle size={10} weight="fill" /> Sesiones
          </div>
          <div className="font-display font-black text-2xl mt-1">{data.sessions_7d_total}</div>
          <div className="text-[10px] text-muted mt-0.5">
            {data.sessions_7d_completed} completas
            {data.sessions_7d_aborted > 0 ? ` · ${data.sessions_7d_aborted} abortadas` : ""}
          </div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted flex items-center gap-1">
            <Bug size={10} weight="fill" /> Errores 7d
          </div>
          <div
            className="font-display font-black text-2xl mt-1"
            style={{ color: data.errors_7d > 0 ? "#FF9500" : "#32D74B" }}
            data-testid="errors-7d-count"
          >
            {data.errors_7d}
          </div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted">Reportes totales</div>
          <div className="font-display font-black text-2xl mt-1">{(data.recent_errors || []).length}</div>
          <button
            onClick={() => navigate("/admin/errors")}
            className="text-[10px] text-primary-app underline mt-1"
            data-testid="open-errors-page"
          >
            Abrir log →
          </button>
        </div>
      </div>

      {data.recent_errors && data.recent_errors.length > 0 ? (
        <div data-testid="recent-errors-list">
          <div className="text-[10px] uppercase tracking-widest text-muted mb-2 flex items-center gap-1">
            <Warning size={10} weight="bold" /> Últimos errores
          </div>
          <div className="space-y-1.5">
            {data.recent_errors.slice(0, 5).map((e) => (
              <div key={e.error_id} className="text-xs bg-[#0A0A0A] border border-app p-2 flex items-start justify-between gap-3" data-testid={`recent-error-${e.error_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-[10px] text-muted">
                    {e.created_at?.slice(0, 19).replace("T", " ")}
                    {e.context ? ` · ${e.context}` : ""}
                    {e.user_name ? ` · ${e.user_name}` : ""}
                  </div>
                  <div className="truncate text-white">{e.message || "—"}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-xs text-muted bg-[#0A0A0A] border border-app p-3 text-center">
          Sin errores reportados. Todo limpio.
        </div>
      )}
    </div>
  );
}
